import {
  analyzeIntegrAIResponse,
  detectIntegrAIInputLanguage,
  getIntegrAIDebugEnabled,
  getIntegrAIResponseContract,
  getLocalizedIntegrAIApology,
  normalizeIntegrAILanguageCode,
} from './integraiLanguage';

const API_BASE = (process.env.REACT_APP_INTEGRAI_API_BASE || '').replace(/\/$/, '');
const API_PREFIX = (process.env.REACT_APP_INTEGRAI_API_PREFIX || '/api').replace(/\/$/, '');
const CLIENT_NAME = 'public-web';
const CLIENT_VERSION = (process.env.REACT_APP_INTEGRAI_CLIENT_VERSION || process.env.REACT_APP_COMMIT_SHA || 'web').trim();
const SESSION_STORAGE_KEY = 'integrai_session_id';

function buildUrl(path) {
  if (!path.startsWith('/')) {
    return `${API_BASE}/${path}`;
  }
  return `${API_BASE}${path}`;
}

function getStoredSessionId() {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY) || '';
  } catch (_err) {
    return '';
  }
}

function storeSessionId(sessionId) {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch (_err) {
    // Ignore storage failures (private mode, disabled storage, etc.)
  }
}

export async function askIntegrAI({
  question,
  userLang = null,
  eventKey = null,
  teamKey = null,
  matchKey = null,
  yearsBack = null,
  k = null,
  signal = null,
}) {
  const questionText = typeof question === 'string' ? question : '';
  const detectedUserLang = normalizeIntegrAILanguageCode(userLang || detectIntegrAIInputLanguage(questionText));
  const shouldTrace = getIntegrAIDebugEnabled();
  const apiUrl = buildUrl(`${API_PREFIX}/ask`);

  async function performRequest({ repairAttempt = false } = {}) {
    const payload = { question: questionText };
    if (eventKey) payload.event_key = eventKey;
    if (teamKey) payload.team_key = teamKey;
    if (matchKey) payload.match_key = matchKey;
    if (yearsBack) payload.years_back = yearsBack;
    if (k) payload.k = k;
    if (detectedUserLang && detectedUserLang !== 'unknown') {
      payload.user_lang = detectedUserLang;
    }
    if (shouldTrace || repairAttempt) {
      payload.trace = true;
    }

    const sessionId = getStoredSessionId();
    let res;
    try {
      res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-IntegrAI-Client': CLIENT_NAME,
          'X-IntegrAI-Client-Version': CLIENT_VERSION,
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify(payload),
        signal,
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        const abortError = new Error('IntegrAI request was cancelled.');
        abortError.cause = err;
        abortError.httpStatus = 0;
        abortError.stage = 'aborted';
        abortError.errorType = 'aborted';
        throw abortError;
      }

      const requestError = new Error('Unable to reach the IntegrAI backend.');
      requestError.cause = err;
      requestError.httpStatus = 0;
      requestError.stage = 'fetch';
      requestError.errorType = 'network_error';
      throw requestError;
    }

    if (!res.ok) {
      const text = await res.text();
      if (res.status === 429) {
        const rateLimitError = new Error('IntegrAI daily prompt limit reached for this device. Try again later.');
        rateLimitError.httpStatus = res.status;
        rateLimitError.stage = 'http';
        rateLimitError.errorType = 'http_4xx';
        throw rateLimitError;
      }
      const responseError = new Error(`IntegrAI backend error ${res.status}: ${text}`);
      responseError.httpStatus = res.status;
      responseError.stage = 'http';
      responseError.errorType = res.status >= 500 ? 'http_5xx' : 'http_4xx';
      throw responseError;
    }

    let data;
    try {
      data = await res.json();
    } catch (err) {
      const parseError = new Error('IntegrAI returned an invalid response.');
      parseError.cause = err;
      parseError.httpStatus = res.status;
      parseError.stage = 'parse';
      parseError.errorType = 'invalid_json';
      throw parseError;
    }

    storeSessionId(data?.session_id);
    const normalizedContract = getIntegrAIResponseContract(data);
    const normalizedSources = Array.isArray(normalizedContract.citations) ? normalizedContract.citations : (Array.isArray(data?.sources) ? data.sources : []);
    const normalizedAnswer = normalizedContract.finalAnswerText || data?.answer || '';
    const responseContract = {
      version: data?.response_contract?.version || 0,
      final_answer_text: normalizedContract.finalAnswerText,
      raw_answer_text: normalizedContract.rawAnswerText,
      requested_language: normalizedContract.requestedLanguage,
      detected_language: normalizedContract.detectedLanguage,
      output_language_valid: normalizedContract.outputLanguageValid,
      answer_status: normalizedContract.answerStatus,
      citations: normalizedSources,
      selected_route: normalizedContract.selectedRoute,
      selected_source_family: normalizedContract.selectedSourceFamily,
      canonical_query: normalizedContract.canonicalQuery,
      translation_error_nonfatal: normalizedContract.translationErrorNonfatal,
      translation_error_fatal: normalizedContract.translationErrorFatal,
      frontend_render_safe: normalizedContract.frontendRenderSafe,
    };
    const normalizedData = {
      ...data,
      answer: normalizedAnswer,
      sources: normalizedSources,
      response_contract: responseContract,
      raw_answer_text: responseContract.raw_answer_text,
      final_answer_text: responseContract.final_answer_text,
      answer_status: responseContract.answer_status,
    };
    const verification = analyzeIntegrAIResponse(questionText, normalizedData, detectedUserLang);
    return {
      ...normalizedData,
      _httpStatus: res.status,
      _requestMeta: {
        apiBase: API_BASE,
        apiPrefix: API_PREFIX,
        apiUrlUsed: apiUrl,
        detectedInputLanguage: detectedUserLang,
        traceRequested: Boolean(payload.trace),
        payloadSent: payload,
        clientName: CLIENT_NAME,
        clientVersion: CLIENT_VERSION,
        repairAttempt,
      },
      _uiVerification: {
        frontendApiUrlUsed: apiUrl,
        detectedInputLanguage: detectedUserLang,
        canonicalEnglishQuery: responseContract.canonical_query || '',
        selectedRoute: verification.selectedRoute,
        selectedSourceFamily: verification.selectedSourceFamily,
        sourceTypes: verification.sourceTypes,
        outputLanguageMatched: verification.outputLanguageMatched,
        answerStatus: verification.answerStatus,
        frontendRenderSafe: verification.frontendRenderSafe,
        routeBlockedTba: verification.questionClass !== 'tba_live' && !verification.tbaLeak,
        usedInputTranslation:
          Boolean(normalizedData?.trace?.translate_in_used) || Boolean(normalizedData?.language?.translation_applied),
        usedOutputTranslation:
          Boolean(normalizedData?.trace?.translate_out_used)
          || Boolean(normalizedData?.language?.output_translation_applied)
          || Boolean(responseContract.translation_error_nonfatal),
        repairAttempted: repairAttempt,
        languageRepairFallbackUsed: false,
        questionClass: verification.questionClass,
        issues: verification.issues,
      },
    };
  }

  const firstResponse = await performRequest();
  const firstVerification = analyzeIntegrAIResponse(questionText, firstResponse, detectedUserLang);
  if (firstVerification.valid) {
    return firstResponse;
  }

  try {
    const repairedResponse = await performRequest({ repairAttempt: true });
    const repairedVerification = analyzeIntegrAIResponse(questionText, repairedResponse, detectedUserLang);
    if (repairedVerification.valid) {
      return {
        ...repairedResponse,
        _uiVerification: {
          ...repairedResponse._uiVerification,
          repairAttempted: true,
          languageRepairFallbackUsed: false,
          issues: firstVerification.issues,
        },
      };
    }

    return buildSafeFallbackResponse({
      baseResponse: repairedResponse,
      detectedUserLang,
      issues: dedupedIssues(firstVerification.issues, repairedVerification.issues),
    });
  } catch (_repairError) {
    return buildSafeFallbackResponse({
      baseResponse: firstResponse,
      detectedUserLang,
      issues: firstVerification.issues,
    });
  }
}

function dedupedIssues(...groups) {
  return [...new Set(groups.flat().filter(Boolean))];
}

function buildSafeFallbackResponse({ baseResponse, detectedUserLang, issues }) {
  const safeLanguage = detectedUserLang === 'unknown' ? 'en' : detectedUserLang;
  return {
    ...baseResponse,
    answer: getLocalizedIntegrAIApology(safeLanguage),
    sources: [],
    response_contract: {
      ...(baseResponse?.response_contract || {}),
      final_answer_text: getLocalizedIntegrAIApology(safeLanguage),
      raw_answer_text: baseResponse?.response_contract?.raw_answer_text || baseResponse?.raw_answer_text || baseResponse?.answer || '',
      requested_language: safeLanguage,
      detected_language: safeLanguage,
      output_language_valid: true,
      answer_status: 'unsupported',
      citations: [],
      selected_route: 'frontend_language_safety_fallback',
      selected_source_family: 'frontend_safety_fallback',
      translation_error_nonfatal: null,
      translation_error_fatal: null,
      frontend_render_safe: true,
    },
    raw_answer_text: baseResponse?.raw_answer_text || baseResponse?.answer || '',
    final_answer_text: getLocalizedIntegrAIApology(safeLanguage),
    answer_status: 'unsupported',
    inferred: {
      ...(baseResponse?.inferred || {}),
      route: 'frontend_language_safety_fallback',
      selected_source_family: 'frontend_safety_fallback',
    },
    language: {
      ...(baseResponse?.language || {}),
      user_language: safeLanguage,
      response_language: safeLanguage,
      output_language_detected: safeLanguage,
      output_language_matched: true,
      output_fallback_used: true,
    },
    _uiVerification: {
      ...(baseResponse?._uiVerification || {}),
      repairAttempted: true,
      languageRepairFallbackUsed: true,
      issues: dedupedIssues(issues),
      selectedRoute: 'frontend_language_safety_fallback',
      selectedSourceFamily: 'frontend_safety_fallback',
      sourceTypes: [],
      outputLanguageMatched: true,
      routeBlockedTba: true,
    },
  };
}
